<template>
  <sidebar-menu :menu="translatedMenu">
    <template #header>
      <AppLogo class="logo" />
      <SelectedSubject />
    </template>
    <template #footer>
      <SubjectSettings />
      <LogoutButton />
      <VersionInfo />
      <TimeToEndSession />
    </template>
  </sidebar-menu>
</template>

<script setup lang="ts">
  import { SidebarMenu, type SidebarItem } from 'vue-sidebar-menu';
  import 'vue-sidebar-menu/dist/vue-sidebar-menu.css';
  import menuItems, { type AppMenuItem } from '@/assets/menu';
  import AppLogo from '@/components/widget/AppLogo.vue';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import VersionInfo from '@/components/widget/VersionInfo.vue';
  import SelectedSubject from '@/components/widget/SelectedSubject.vue';
  import SubjectSettings from '@/components/widget/SubjectSettings.vue';
  import LogoutButton from '@/components/widget/LogoutButton.vue';
  import TimeToEndSession from '@/components/widget/TimeToEndSession.vue';
  import { useInteractionModeStore } from '@/store/interactionModeStore';
  import { InteractionModeEnum } from '@/types/InteractionModeEnum';

  const { t } = useI18n();
  const interactionModeStore = useInteractionModeStore();
  const isEditMode = computed(() => interactionModeStore.mode === InteractionModeEnum.EDIT);

  // Function to translate menu titles
  const translateMenu = (menu: AppMenuItem[], parentId = 'menu'): SidebarItem[] => {
    return menu.map((item, index) => {
      const { requiresEditMode, ...sidebarItem } = item;
      const itemId = item.id || `${parentId}-${index}`;
      const translatedItem: SidebarItem = {
        ...sidebarItem,
        id: itemId,
        title: t(item.title),
        hidden: item.hidden || (requiresEditMode && !isEditMode.value),
      };
      if (item.child) {
        translatedItem.child = translateMenu(item.child, itemId);
      }
      return translatedItem;
    });
  };

  const translatedMenu = computed(() => translateMenu(menuItems));
</script>

<style scoped lang="scss">
  .v-sidebar-menu {
    --vsm-primary-color: #{$primary2Color};
    --vsm-base-bg: #{$secondaryColor};
    --vsm-item-color: #fff;
    --vsm-item-active-line-color: var(--vsm-primary-color);
    --vsm-item-open-color: #fff;
    --vsm-item-open-bg: var(--vsm-primary-color);
    --vsm-item-hover-bg: #{$overlay2Color};
    --vsm-icon-color: var(--vsm-item-color);
    --vsm-icon-bg: #1e1e21;
    --vsm-mobile-item-color: #fff;
    --vsm-mobile-item-bg: var(--vsm-primary-color);
    --vsm-mobile-icon-color: var(--vsm-mobile-item-color);
    --vsm-mobile-icon-bg: transparent;
    --vsm-dropdown-bg: #{$darkColor};
    --vsm-header-item-color: rgba(255, 255, 255, 0.7);
    --vsm-toggle-btn-color: #fff;
    --vsm-toggle-btn-bg: #1e1e21;
    --vsm-item-font-size: 1em;
    --vsm-item-line-height: 35px;
    --vsm-item-padding: 0.5em 1em;
    --vsm-icon-height: 35px;
    --vsm-icon-width: 35px;
  }
</style>
